import React from "react";
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Collapse,
  message,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import classes from "./ScenarioDetailsPage.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  defaultCategoriesRisksOpportunity,
  priorityLevels,
  queryKeys,
  scenarioTypes,
  statuses,
} from "../../constants";
import {
  fetchIndividualScenarios,
  generateStrategy,
  riskOpportunityDetails,
} from "../../services/scenarios";
import Loader from "../../components/Loading";
import { FundViewOutlined } from "@ant-design/icons";
import { getThemeColor } from "../../helpers";

const { Title } = Typography;

const RenderItemValue = ({ label, value }) => {
  return (
    <div className={classes.itemContainer}>
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{value ?? "-"}</span>
    </div>
  );
};

const ScenarioDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [postData, setPostData] = React.useState({
    risk_opportunity_ids: [],
    category: "",
  });

  const handleCheck = (category, item) => {
    const index = postData.risk_opportunity_ids.findIndex(
      (item) => item === item.id
    );
    if (index === -1) {
      setPostData({
        risk_opportunity_ids:
          postData.category === category
            ? [...postData.risk_opportunity_ids, item.id]
            : [item.id],
        category,
      });
    } else {
      setPostData({
        risk_opportunity_ids:
          postData.category === category
            ? postData.risk_opportunity_ids.filter((item) => item !== item.id)
            : [item.id],
        category,
      });
    }
  };

  const getScenarioDetailsQuery = useQuery({
    queryKey: [queryKeys.scenarioDetails, id],
    queryFn: () => fetchIndividualScenarios(id),
    enabled: true,
  });

  const getRisksOpportunityQuery = useQuery({
    queryKey: [queryKeys.riskOpportunity, id],
    queryFn: () => riskOpportunityDetails(id),
    enabled: true,
  });

  const createStrategy = useMutation({
    mutationFn: generateStrategy,
    onSuccess: (response) => {
      getScenarioDetailsQuery.refetch();
      message.success(
        response.data?.message ??
          "Strategy has been generating in the background!"
      );
    },
    onError: (error) => {
      message.error(error?.response?.data?.message ?? "Something went wrong");
    },
  });

  const data = getScenarioDetailsQuery.data?.data ?? {};

  const status = statuses.find((status) => status.value === data.status);

  const scenarioType = scenarioTypes.find(
    (type) => type.value === data.scenario_type
  );
  const priorityLevel = priorityLevels.find(
    (level) => level.value === data.priority_level
  );

  const getRisk = (category) => {
    return getRisksOpportunityQuery?.data?.data?.filter(
      (risk) => risk.category_name === category
    );
  };

  const getOpportunity = (category) =>
    getRisksOpportunityQuery?.data?.data?.filter(
      (opportunity) =>
        opportunity.category_name === category &&
        opportunity.type === "Opportunity"
    );

  const getColor = (urgency) => {
    switch (urgency) {
      case "High Priority":
        return "red";
      case "Urgent":
        return "warning";
      case "Strategic":
        return "blue";
      default:
        return "grey";
    }
  };

  const risksNested = (category, risks) => [
    {
      key: "1",
      label: "Risks",
      children: (
        <div>
          {risks.map((risk) => (
            <div key={risk.id} style={{ display: "flex", gap: "10px" }}>
              <Checkbox
                onChange={() => handleCheck(category, risk)}
                checked={
                  postData.risk_opportunity_ids.includes(risk.id) ||
                  risk.is_executed
                }
                disabled={risk.is_executed}
              />
              <div className={classes.containerItem}>
                <span className={classes.titleContainer}>
                  Name{" "}
                  <span className={classes.valueContainer}>{risk.name}</span>{" "}
                  <Tag bordered={false} color={getColor(risk.urgency)}>
                    {risk.urgency}
                  </Tag>
                  <Tooltip title="">
                    <FundViewOutlined
                      style={{
                        color: getThemeColor(),
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                    />
                  </Tooltip>
                </span>
                <span className={classes.titleContainer}>
                  {" "}
                  Description{" "}
                  <span className={classes.valueContainer}>
                    {risk.description}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
      ),
    },
  ];

  const opportunityNested = (category, opportunities) => [
    {
      key: "1",
      label: "Opportunities",
      children: (
        <div>
          {opportunities.map((opportunity) => (
            <div key={opportunity.id} style={{ display: "flex", gap: "10px" }}>
              <Checkbox
                onChange={() => handleCheck(category, opportunity)}
                checked={
                  postData.risk_opportunity_ids.includes(opportunity.id) ||
                  opportunity.is_executed
                }
                disabled={opportunity.is_executed}
              />
              <div className={classes.containerItem}>
                <span className={classes.titleContainer}>
                  Name{" "}
                  <span className={classes.valueContainer}>
                    {opportunity.name}
                  </span>{" "}
                  <Tag bordered={false} color={getColor(opportunity.urgency)}>
                    {opportunity.urgency}
                  </Tag>
                  <Tooltip title="">
                    <FundViewOutlined
                      style={{
                        color: getThemeColor(),
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                    />
                  </Tooltip>
                </span>
                <span className={classes.titleContainer}>
                  {" "}
                  Description{" "}
                  <span className={classes.valueContainer}>
                    {opportunity.description}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
      ),
    },
  ];

  const handleCreateStrategy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    createStrategy.mutate({
      id,
      postData,
    });
  };

  const handleViewStrategicPlan = (risks) => {
    navigate(`/home/risk-opportunity/${id}/${risks?.[0]?.category ?? ""}`);
  };

  return getScenarioDetailsQuery.isLoading ||
    getRisksOpportunityQuery.isLoading ? (
    <Loader />
  ) : (
    <>
      <div className={classes.header}>
        <Row gutter={[8, 8]} align={"middle"}>
          <div className={classes.headingContainer}>
            <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
              Objective Details
            </Title>
          </div>
        </Row>
        <Row
          gutter={[8, 24]}
          align={"middle"}
          style={{ marginTop: 20, marginBottom: 20 }}
        >
          <Col sm={24} md={24}>
            <RenderItemValue label="Objective Name" value={data.title} />
          </Col>

          <Col sm={24} md={24}>
            <RenderItemValue label="Objectives" value={data.objectives} />
          </Col>

          <Col sm={24} md={6}>
            <RenderItemValue
              label="Status"
              value={
                <Badge
                  color={status?.color ?? "grey"}
                  text={status?.label ?? data.status}
                />
              }
            />
          </Col>
          <Col sm={24} md={6}>
            <RenderItemValue
              label="Objective Type"
              value={scenarioType.label}
            />
          </Col>
          <Col sm={24} md={6}>
            <RenderItemValue
              label="Priority Level"
              value={priorityLevel.label}
            />
          </Col>
          <Col sm={24} md={6}>
            <RenderItemValue
              label="Budget Estimate"
              value={data.budget_estimate}
            />
          </Col>
          <Col sm={24} md={24}>
            <RenderItemValue label="Plan" value={data.plan} />
          </Col>
          <Col sm={24} md={24}>
            <RenderItemValue label="Dependencies" value={data.dependencies} />
          </Col>
        </Row>

        <Row gutter={[8, 8]} align={"middle"}>
          <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
            Risks & Opportunities Categories
          </Title>
        </Row>

        <Space
          direction="vertical"
          style={{
            margin: "10px 0",
            width: "100%",
          }}
        >
          {defaultCategoriesRisksOpportunity.map((category) => {
            const risks = getRisk(category);
            const opportunities = getOpportunity(category);

            const isExecuted =
              risks?.some((risk) => risk.is_executed) ||
              opportunities?.some((opportunity) => opportunity.is_executed);

            const createStrategyButtonEnable = postData.category === category;

            return (
              <Collapse
                collapsible="header"
                defaultActiveKey={["Financial"]}
                expandIconPosition={"end"}
                items={[
                  {
                    key: category,
                    label: category,
                    children: (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      >
                        <Collapse
                          items={risksNested(category, risks)}
                          bordered={false}
                          expandIconPosition={"end"}
                        />
                        <Collapse
                          items={opportunityNested(category, opportunities)}
                          bordered={false}
                          expandIconPosition={"end"}
                        />
                      </div>
                    ),
                    extra: (
                      <>
                        {!isExecuted && (
                          <Button
                            onClick={handleCreateStrategy}
                            size="small"
                            color="primary"
                            variant="outlined"
                            style={{
                              fontSize: 12,
                            }}
                            disabled={!createStrategyButtonEnable}
                          >
                            Create Strategy
                          </Button>
                        )}
                        {isExecuted && (
                          <Button
                            onClick={() => handleViewStrategicPlan(risks)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            style={{
                              fontSize: 12,
                            }}
                          >
                            View Strategic Plan
                          </Button>
                        )}
                      </>
                    ),
                  },
                ]}
              />
            );
          })}
        </Space>
      </div>
    </>
  );
};
export default ScenarioDetailsPage;
